/**
 * Birth Event Utility Functions
 * Handles time calculations, data transformations, and validation for birth events
 */

/**
 * Normalizes a timestamp string to ensure it has a timezone indicator.
 * After the PostgreSQL migration, the backend may return UTC timestamps without
 * the 'Z' suffix (e.g., "2026-02-09T00:32:48" instead of "2026-02-09T00:32:48Z").
 * Per ECMAScript, `new Date("2026-02-09T00:32:48")` (without timezone) is parsed
 * as LOCAL time, causing UTC values to display incorrectly.
 *
 * This function ensures all datetime strings have explicit timezone indicators
 * so downstream `new Date()` calls parse them correctly.
 *
 * @param {*} val - The timestamp value to normalize
 * @returns {*} The normalized timestamp string, or the original value if not a datetime string
 */
export const normalizeTimestamp = (val) => {
  if (!val || typeof val !== 'string') return val;

  // If already has 'Z' suffix, it's correctly marked as UTC → no change
  if (val.endsWith('Z')) return val;

  // WORKAROUND for backend timezone bug (post-PostgreSQL migration):
  // The backend incorrectly applies the server's local timezone offset to UTC values.
  // Example: frontend sends "2026-02-08T19:32:48.000Z" (19:32 UTC = 14:32 EST).
  // Backend returns "2026-02-08T19:32:48-05:00" — the raw UTC hour (19:32) with
  // the server's EST offset (-05:00) appended WITHOUT converting the hours.
  // This makes JS interpret it as 19:32 EST (= 00:32+1 UTC), which is 5 hours off.
  //
  // Fix: strip the timezone offset and treat the raw datetime as UTC by appending 'Z'.
  // The raw hour value IS the correct UTC hour; the offset is the bug.
  const offsetMatch = val.match(/^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)[+-]\d{2}(?::\d{2})?$/);
  if (offsetMatch) {
    return offsetMatch[1].replace(' ', 'T') + 'Z';
  }

  // ISO format with 'T' separator but no timezone → assume UTC, add 'Z'
  // Matches: "2026-02-09T00:32:48", "2026-02-09T00:32:48.000", "2026-02-09T00:32:48.0000000"
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?$/.test(val)) {
    return val + 'Z';
  }

  // PostgreSQL native format with space separator → convert to ISO and add 'Z'
  // Matches: "2026-02-09 00:32:48", "2026-02-09 00:32:48.000"
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(?::\d{2})?(?:\.\d+)?$/.test(val)) {
    return val.replace(' ', 'T') + 'Z';
  }

  return val;
};

/**
 * Extracts time from a DateTime string and converts to local timezone
 * @param {string} dateTimeString - Full DateTime or time string
 * @returns {string} Time in HH:mm:ss format (local timezone)
 */
export const extractTimeFromDateTime = (dateTimeString) => {
  if (!dateTimeString) return "";
  
  // If it's already in time format (HH:mm or HH:mm:ss), return as is
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(dateTimeString)) {
    return dateTimeString;
  }
  
  // If it's a full DateTime string, extract the time portion in local timezone
  try {
    // Normalize the timestamp to ensure timezone indicator is present
    const dateString = normalizeTimestamp(dateTimeString);
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    
    // Get hours, minutes, seconds in local timezone
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    
    return `${hours}:${minutes}:${seconds}`;
  } catch (err) {
    console.error('Error extracting time from DateTime:', err);
    return "";
  }
};

/**
 * Calculates time difference between two times
 * @param {string} startTime - Start time
 * @param {string} endTime - End time
 * @returns {string|null} Duration string like "5 min 30 sec"
 */
export const calculateTimeDifference = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  
  const start = new Date(`1970-01-01T${extractTimeFromDateTime(startTime) || startTime}`);
  const end = new Date(`1970-01-01T${extractTimeFromDateTime(endTime) || endTime}`);
  const diffMs = end - start;
  
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  
  return `${minutes} min ${seconds} sec`;
};

/**
 * Formats time for API submission
 * @param {string} timeValue - Time value to format
 * @param {string} dateOfBirth - Date context for the time (optional, defaults to today)
 * @returns {string|null} Formatted DateTime string or null
 */
export const formatTimeForAPI = (timeValue, dateOfBirth) => {
  if (!timeValue) return null;
  
  // If timeValue is already a full DateTime string, return as is
  if (typeof timeValue === 'string' && timeValue.includes('T')) {
    return timeValue;
  }
  
  // Extract just the time portion if needed
  const timeOnly = typeof timeValue === 'string' && timeValue.includes('T') 
    ? timeValue.split('T')[1] 
    : timeValue;
  
  // Use provided date or default to today
  const dateToUse = dateOfBirth || new Date().toISOString().split('T')[0];
  
  // Combine date and time
  if (timeOnly && typeof timeOnly === 'string') {
    return `${dateToUse}T${timeOnly}`;
  }
  
  return null;
};

/**
 * Formats a timestamp for display
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted date string
 */
export const formatNoteTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Finds the next labor stage start time
 * @param {string} currentStageName - Current stage name
 * @param {Object} values - All labor stage values
 * @returns {string|null} Next stage start time or null
 */
export const getNextStageStart = (currentStageName, values) => {
  const stageOrder = [
    'earlyLaborStart',
    'activeLaborStart',
    'secondStageStart',
    'thirdStageStart',
    'placentaDeliveryTime'
  ];
  
  const currentIndex = stageOrder.indexOf(currentStageName);
  if (currentIndex === -1 || currentIndex === stageOrder.length - 1) return null;
  
  return values[stageOrder[currentIndex + 1]];
};

/**
 * Calculates total labor duration
 * @param {Object} values - Labor stage values
 * @returns {Object|null} Object with hours and minutes
 */
export const calculateTotalDuration = (values) => {
  if (!values.earlyLaborStart) return null;

  // Parse datetimes — new Date() already handles UTC→local internally.
  // No manual timezone offset adjustment needed; the difference between
  // two Date objects is timezone-agnostic (both represent UTC instants).
  const startTime = new Date(values.earlyLaborStart);
  const endTime = values.placentaDeliveryTime ? new Date(values.placentaDeliveryTime) : new Date();

  if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return null;

  const diffMinutes = Math.floor((endTime - startTime) / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  
  return { hours, minutes };
};

/**
 * Converts weight from grams to US units (lbs and oz)
 * @param {number} weightInGrams - Weight in grams
 * @returns {Object} Object with lbs and oz
 */
export const convertGramsToUS = (weightInGrams) => {
  if (!weightInGrams) return { lbs: 0, oz: 0 };
  
  const totalLbs = weightInGrams * 0.00220462;
  const lbs = Math.floor(totalLbs);
  const oz = Math.round((totalLbs % 1) * 16);
  
  return { lbs, oz };
};

/**
 * Converts US weight to grams
 * @param {number} lbs - Pounds
 * @param {number} oz - Ounces
 * @returns {number} Weight in grams
 */
export const convertUSToGrams = (lbs = 0, oz = 0) => {
  return (parseFloat(lbs) * 453.592) + (parseFloat(oz) * 28.3495);
};

/**
 * Converts kg to grams
 * @param {number} kg - Weight in kilograms
 * @returns {number} Weight in grams
 */
export const convertKgToGrams = (kg = 0) => {
  return parseFloat(kg) * 1000;
};

/**
 * Maps loaded birth event data to form format
 * @param {Object} birthEventData - Birth event data from API
 * @param {Object} currentFormData - Current form data
 * @returns {Object} Mapped form data
 */
export const mapBirthEventToForm = (birthEventData, currentFormData) => {
  const { lbs, oz } = convertGramsToUS(birthEventData.weightInGrams);
  
  return {
    // Keep existing data for fields not explicitly mapped below
    ...currentFormData,
    
    // Basic baby info (these will override currentFormData)
    firstName: birthEventData.firstName || currentFormData.firstName,
    lastName: birthEventData.lastName || currentFormData.lastName,
    middleName: birthEventData.middleName || currentFormData.middleName,
    sex: birthEventData.sex || currentFormData.sex,
    
    // Birth date/time — use local timezone conversion, not naive .split('T')
    // PostgreSQL stores timestamptz as UTC; extractTimeFromDateTime handles UTC→local properly
    dateOfBirth: birthEventData.birthDateTime ? (() => {
      try {
        let str = birthEventData.birthDateTime;
        // If no timezone indicator, assume UTC (same logic as extractTimeFromDateTime)
        if (str.includes('T') && !str.includes('Z') && !/[+-]\d{2}(:\d{2})?$/.test(str)) str += 'Z';
        const dt = new Date(str);
        if (isNaN(dt.getTime())) return currentFormData.dateOfBirth;
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      } catch { return currentFormData.dateOfBirth; }
    })() : currentFormData.dateOfBirth,
    timeOfBirth: birthEventData.birthDateTime
      ? (extractTimeFromDateTime(birthEventData.birthDateTime) || currentFormData.timeOfBirth)
      : currentFormData.timeOfBirth,
    
    // Measurements
    weightKg: birthEventData.weightInGrams ? (birthEventData.weightInGrams / 1000).toFixed(2) : currentFormData.weightKg,
    weightLbs: lbs || currentFormData.weightLbs,
    weightOz: oz || currentFormData.weightOz,
    birthLength: birthEventData.lengthInCm || currentFormData.birthLength,
    
    // Labor stages
    earlyLaborStart: birthEventData.earlyLaborStart || currentFormData.earlyLaborStart,
    activeLaborStart: birthEventData.activeLaborStart || currentFormData.activeLaborStart,
    secondStageStart: birthEventData.secondStageStart || currentFormData.secondStageStart,
    thirdStageStart: birthEventData.thirdStageStart || currentFormData.thirdStageStart,
    
    // Critical times (Cephalic)
    pushingStart: extractTimeFromDateTime(birthEventData.pushingStart) || currentFormData.pushingStart,
    crowning: extractTimeFromDateTime(birthEventData.crowning) || currentFormData.crowning,
    headBornTime: extractTimeFromDateTime(birthEventData.headBornTime) || currentFormData.headBornTime,
    bodyBornTime: extractTimeFromDateTime(birthEventData.bodyBornTime) || currentFormData.bodyBornTime,
    firstBreathTime: extractTimeFromDateTime(birthEventData.firstBreathTime) || currentFormData.firstBreathTime,
    placentaDeliveryTime: extractTimeFromDateTime(birthEventData.placentaDeliveryTime) || currentFormData.placentaDeliveryTime,
    pushingDuration: birthEventData.pushingDuration || currentFormData.pushingDuration,
    
    // Birth presentation
    birthPresentation: birthEventData.birthPresentation || currentFormData.birthPresentation || 'Cephalic',
    
    // Breech timing fields
    buttocksVisible: extractTimeFromDateTime(birthEventData.buttocksVisible) || currentFormData.buttocksVisible,
    buttocksBorn: extractTimeFromDateTime(birthEventData.buttocksBorn) || currentFormData.buttocksBorn,
    trunkToUmbilicus: extractTimeFromDateTime(birthEventData.trunkToUmbilicus) || currentFormData.trunkToUmbilicus,
    trunkToAxillae: extractTimeFromDateTime(birthEventData.trunkToAxillae) || currentFormData.trunkToAxillae,
    armsDelivered: extractTimeFromDateTime(birthEventData.armsDelivered) || currentFormData.armsDelivered,
    headVisible: extractTimeFromDateTime(birthEventData.headVisible) || currentFormData.headVisible,
    breechType: birthEventData.breechType || currentFormData.breechType,
    breechExtractionMethod: birthEventData.breechExtractionMethod || currentFormData.breechExtractionMethod,
    breechHeadDelivery: birthEventData.breechHeadDelivery || currentFormData.breechHeadDelivery,
    breechComplications: birthEventData.breechComplications || currentFormData.breechComplications,
    breechNotes: birthEventData.breechNotes || currentFormData.breechNotes,
    
    // Birth details
    presentation: birthEventData.presentation || currentFormData.presentation,
    birthPosition: birthEventData.birthPosition || currentFormData.birthPosition,
    birthMethod: birthEventData.birthMethod || currentFormData.birthMethod,
    birthPlace: birthEventData.birthPlace || currentFormData.birthPlace,
    
    // APGAR scores
    apgar1Min: birthEventData.apgarScores?.[0] || currentFormData.apgar1Min,
    apgar5Min: birthEventData.apgarScores?.[1] || currentFormData.apgar5Min,
    apgar10Min: birthEventData.apgarScores?.[2] || currentFormData.apgar10Min,
    
    // Water usage
    waterBirth: birthEventData.waterBirth ? "Yes" : "No",
    waterPainRelief: birthEventData.waterPainRelief ? "Yes" : "No",
    
    // Resuscitation
    ppv: birthEventData.resuscitationMeasures?.includes("PPV") ? "Yes" : "No",
    oxygen: birthEventData.resuscitationMeasures?.includes("Oxygen") ? "Yes" : "No",
    chestCompressions: birthEventData.resuscitationMeasures?.includes("Chest Compressions") ? "Yes" : "No",
    
    // Notes
    laborNotes: birthEventData.laborNotes || currentFormData.laborNotes,
    birthNotes: birthEventData.birthNotes || currentFormData.birthNotes,
    
    // Other fields
    nuchal: birthEventData.nuchal || currentFormData.nuchal,
    suction: birthEventData.requiredSuction ? "DeLee" : "None",
    
    // Immediate Newborn Exam fields
    gestationalAge: birthEventData.gestationalAge || currentFormData.gestationalAge,
    twinType: birthEventData.twinType || currentFormData.twinType,
    chorionicity: birthEventData.chorionicity || currentFormData.chorionicity,
    headCircumference: birthEventData.headCircumference || currentFormData.headCircumference,
    shoulderCircumference: birthEventData.shoulderCircumference || currentFormData.shoulderCircumference,
    chestCircumference: birthEventData.chestCircumference || currentFormData.chestCircumference,
    abdominalCircumference: birthEventData.abdominalCircumference || currentFormData.abdominalCircumference,
    
    // Cord Management
    cordClamping: birthEventData.cordClamping || currentFormData.cordClamping,
    cordClampedTime: birthEventData.cordClampedTime || currentFormData.cordClampedTime,
    cordBloodCollected: birthEventData.cordBloodCollected || currentFormData.cordBloodCollected,
    cordBloodPurpose: birthEventData.cordBloodPurpose || currentFormData.cordBloodPurpose,
    
    // Physical Exam
    generalAppearance: birthEventData.generalAppearance || currentFormData.generalAppearance,
    heartRate: birthEventData.heartRate || currentFormData.heartRate,
    respiratoryRate: birthEventData.respiratoryRate || currentFormData.respiratoryRate,
    temperature: birthEventData.temperature || currentFormData.temperature,
    skin: birthEventData.skin || currentFormData.skin,
    head: birthEventData.head || currentFormData.head,
    mouth: birthEventData.mouth || currentFormData.mouth,
    heartLungs: birthEventData.heartLungs || currentFormData.heartLungs,
    abdomen: birthEventData.abdomen || currentFormData.abdomen,
    genitalia: birthEventData.genitalia || currentFormData.genitalia,
    extremities: birthEventData.extremities || currentFormData.extremities,
    anus: birthEventData.anus || currentFormData.anus,
    
    // Physical Exam Normal/Abnormal Toggles
    generalAppearanceNormal: birthEventData.generalAppearanceNormal || currentFormData.generalAppearanceNormal,
    generalAppearanceAbnormal: birthEventData.generalAppearanceAbnormal || currentFormData.generalAppearanceAbnormal,
    skinNormal: birthEventData.skinNormal || currentFormData.skinNormal,
    skinAbnormal: birthEventData.skinAbnormal || currentFormData.skinAbnormal,
    headNormal: birthEventData.headNormal || currentFormData.headNormal,
    headAbnormal: birthEventData.headAbnormal || currentFormData.headAbnormal,
    mouthNormal: birthEventData.mouthNormal || currentFormData.mouthNormal,
    mouthAbnormal: birthEventData.mouthAbnormal || currentFormData.mouthAbnormal,
    heartLungsNormal: birthEventData.heartLungsNormal || currentFormData.heartLungsNormal,
    heartLungsAbnormal: birthEventData.heartLungsAbnormal || currentFormData.heartLungsAbnormal,
    abdomenNormal: birthEventData.abdomenNormal || currentFormData.abdomenNormal,
    abdomenAbnormal: birthEventData.abdomenAbnormal || currentFormData.abdomenAbnormal,
    genitaliaNormal: birthEventData.genitaliaNormal || currentFormData.genitaliaNormal,
    genitaliaAbnormal: birthEventData.genitaliaAbnormal || currentFormData.genitaliaAbnormal,
    extremitiesNormal: birthEventData.extremitiesNormal || currentFormData.extremitiesNormal,
    extremitiesAbnormal: birthEventData.extremitiesAbnormal || currentFormData.extremitiesAbnormal,
    anusNormal: birthEventData.anusNormal || currentFormData.anusNormal,
    anusAbnormal: birthEventData.anusAbnormal || currentFormData.anusAbnormal,
    
    // Newborn Medications
    vitaminK: birthEventData.vitaminK || currentFormData.vitaminK,
    vitaminKDose: birthEventData.vitaminKDose || currentFormData.vitaminKDose,
    vitaminKRoute: birthEventData.vitaminKRoute || currentFormData.vitaminKRoute,
    vitaminKTime: birthEventData.vitaminKTime || currentFormData.vitaminKTime,
    eyeOintment: birthEventData.eyeOintment || currentFormData.eyeOintment,
    eyeOintmentTime: birthEventData.eyeOintmentTime || currentFormData.eyeOintmentTime,
    medicationConsent: birthEventData.medicationConsent || currentFormData.medicationConsent,
    newbornMedicationsAdministered: birthEventData.newbornMedicationsAdministered || currentFormData.newbornMedicationsAdministered || [],
    
    // Feeding
    feedingInitiated: birthEventData.feedingInitiated || currentFormData.feedingInitiated,
    feedingMethod: birthEventData.feedingMethod || currentFormData.feedingMethod,
    latchNotes: birthEventData.latchNotes || currentFormData.latchNotes,
    firstLatchTime: birthEventData.firstLatchTime || currentFormData.firstLatchTime
  };
};

/**
 * Prepares birth event data for API submission
 * @param {Object} baby - Baby form data
 * @param {Object} weightUnit - Weight unit preference
 * @param {boolean} isDischarged - Discharge status
 * @param {string} dischargeReason - Discharge reason
 * @param {Object} birthEvent - Birth event data
 * @param {boolean} isUpdate - Whether this is an update (vs create)
 * @returns {Object} Formatted data for API
 */
export const prepareBirthEventForAPI = (baby, weightUnit, isDischarged, dischargeReason, birthEvent, isUpdate = false) => {
  const isBreech = baby.birthPresentation === 'Breech';
  
  const payload = {
    // Baby creation/linking fields (optional for false labor)
    firstName: baby.firstName || null,
    lastName: baby.lastName || null,
    middleName: baby.middleName || null,
    sex: baby.sex || null,
    birthDate: baby.dateOfBirth && baby.timeOfBirth ? 
      `${baby.dateOfBirth}T${baby.timeOfBirth}` : null,
    isTwin: baby.isTwin || false,
    twinOrder: baby.twinOrder || null,
    
    // False labor fields
    isFalseLabor: isDischarged || false,
    dischargeReason: dischargeReason || null,
    dischargeDateTime: birthEvent?.dischargeDate || null,
    
    // Labor stages
    earlyLaborStart: baby.earlyLaborStart || null,
    activeLaborStart: baby.activeLaborStart || null,
    secondStageStart: baby.secondStageStart || null,
    thirdStageStart: baby.thirdStageStart || null,
    placentaDeliveryTime: formatTimeForAPI(baby.placentaDeliveryTime, baby.dateOfBirth),
    
    // Critical times (Cephalic)
    pushingStart: formatTimeForAPI(baby.pushingStart, baby.dateOfBirth),
    crowning: formatTimeForAPI(baby.crowning, baby.dateOfBirth),
    headBornTime: formatTimeForAPI(baby.headBornTime, baby.dateOfBirth),
    bodyBornTime: formatTimeForAPI(baby.bodyBornTime, baby.dateOfBirth),
    firstBreathTime: formatTimeForAPI(baby.firstBreathTime, baby.dateOfBirth),
    pushingDuration: baby.pushingDuration || null,
    
    // Birth presentation
    birthPresentation: baby.birthPresentation || 'Cephalic',
    
    // Breech-specific fields
    ...(isBreech && {
      buttocksVisible: formatTimeForAPI(baby.buttocksVisible, baby.dateOfBirth),
      buttocksBorn: formatTimeForAPI(baby.buttocksBorn, baby.dateOfBirth),
      trunkToUmbilicus: formatTimeForAPI(baby.trunkToUmbilicus, baby.dateOfBirth),
      trunkToAxillae: formatTimeForAPI(baby.trunkToAxillae, baby.dateOfBirth),
      armsDelivered: formatTimeForAPI(baby.armsDelivered, baby.dateOfBirth),
      headVisible: formatTimeForAPI(baby.headVisible, baby.dateOfBirth),
      breechType: baby.breechType || null,
      breechExtractionMethod: baby.breechExtractionMethod || null,
      breechHeadDelivery: baby.breechHeadDelivery || null,
      breechComplications: baby.breechComplications || null,
      breechNotes: baby.breechNotes || null,
    }),

    // Birth details (fetal position only applies to cephalic births)
    presentation: isBreech ? null : (baby.presentation || "Vertex"),
    birthPosition: baby.birthPosition || "OA",
    birthMethod: baby.birthMethod || "Vaginal",
    birthPlace: baby.birthPlace || "Home",
    
    // Measurements
    weightInGrams: weightUnit.unit === "US" 
      ? convertUSToGrams(baby.weightLbs, baby.weightOz)
      : convertKgToGrams(baby.weightKg),
    lengthInCm: parseFloat(baby.birthLength || 0) || null,
    
    // APGAR scores
    apgarScores: [
      parseInt(baby.apgar1Min || 0),
      parseInt(baby.apgar5Min || 0),
      parseInt(baby.apgar10Min || 0)
    ],

    // Conditions
    nuchal: baby.nuchal || "None",
    nuchalCordType: baby.nuchalCordType || null,
    requiredSuction: baby.suction === "DeLee",
    waterBirth: baby.waterBirth === "Yes",
    waterLaborOnly: baby.waterFirstStage === "Yes" || baby.waterSecondStage === "Yes",
    waterPainRelief: baby.waterPainRelief === "Yes",

    // Resuscitation
    requiredResuscitation: baby.ppv === "Yes" || baby.oxygen === "Yes" || baby.chestCompressions === "Yes",
    resuscitationMeasures: [
      ...(baby.ppv === "Yes" ? ["PPV"] : []),
      ...(baby.oxygen === "Yes" ? ["Oxygen"] : []),
      ...(baby.chestCompressions === "Yes" ? ["Chest Compressions"] : [])
    ],

    // Notes
    laborNotes: typeof baby.laborNotes === 'string' ? 
      (baby.laborNotes.trim() ? JSON.parse(baby.laborNotes) : []) : 
      (baby.laborNotes || []),
    birthNotes: baby.birthNotes || null,
    
    // Breach details
    isBreech,
    breechType: baby.breechType || null,
    breechManeuvers: baby.breechExtractionMethod ? [baby.breechExtractionMethod] : null,
    
    // Immediate Newborn Exam fields
    gestationalAge: baby.gestationalAge || null,
    twinType: baby.twinType || null,
    chorionicity: baby.chorionicity || null,
    headCircumference: baby.headCircumference || null,
    shoulderCircumference: baby.shoulderCircumference || null,
    chestCircumference: baby.chestCircumference || null,
    abdominalCircumference: baby.abdominalCircumference || null,
    
    // Cord Management
    cordClamping: baby.cordClamping || null,
    cordClampedTime: baby.cordClampedTime || null,
    cordBloodCollected: baby.cordBloodCollected || null,
    cordBloodPurpose: baby.cordBloodPurpose || null,
    
    // Physical Exam
    generalAppearance: baby.generalAppearance || null,
    heartRate: baby.heartRate || null,
    respiratoryRate: baby.respiratoryRate || null,
    temperature: baby.temperature || null,
    skin: baby.skin || null,
    head: baby.head || null,
    mouth: baby.mouth || null,
    heartLungs: baby.heartLungs || null,
    abdomen: baby.abdomen || null,
    genitalia: baby.genitalia || null,
    extremities: baby.extremities || null,
    anus: baby.anus || null,
    
    // Physical Exam Normal/Abnormal Toggles
    generalAppearanceNormal: baby.generalAppearanceNormal || false,
    generalAppearanceAbnormal: baby.generalAppearanceAbnormal || false,
    skinNormal: baby.skinNormal || false,
    skinAbnormal: baby.skinAbnormal || false,
    headNormal: baby.headNormal || false,
    headAbnormal: baby.headAbnormal || false,
    mouthNormal: baby.mouthNormal || false,
    mouthAbnormal: baby.mouthAbnormal || false,
    heartLungsNormal: baby.heartLungsNormal || false,
    heartLungsAbnormal: baby.heartLungsAbnormal || false,
    abdomenNormal: baby.abdomenNormal || false,
    abdomenAbnormal: baby.abdomenAbnormal || false,
    genitaliaNormal: baby.genitaliaNormal || false,
    genitaliaAbnormal: baby.genitaliaAbnormal || false,
    extremitiesNormal: baby.extremitiesNormal || false,
    extremitiesAbnormal: baby.extremitiesAbnormal || false,
    anusNormal: baby.anusNormal || false,
    anusAbnormal: baby.anusAbnormal || false,
    
    // Newborn Medications
    vitaminK: baby.vitaminK || null,
    vitaminKDose: baby.vitaminKDose || null,
    vitaminKRoute: baby.vitaminKRoute || null,
    vitaminKTime: baby.vitaminKTime || null,
    eyeOintment: baby.eyeOintment || null,
    eyeOintmentTime: baby.eyeOintmentTime || null,
    medicationConsent: baby.medicationConsent || null,
    newbornMedicationsAdministered: baby.newbornMedicationsAdministered || [],
    
    // Feeding
    feedingInitiated: baby.feedingInitiated || null,
    feedingMethod: baby.feedingMethod || null,
    latchNotes: baby.latchNotes || null,
    firstLatchTime: baby.firstLatchTime || null
  };
  
  return payload;
};

/**
 * Validates weight input
 * @param {Object} baby - Baby form data
 * @param {Object} weightUnit - Weight unit preference
 * @returns {boolean} Whether weight is valid
 */
export const validateWeight = (baby, weightUnit) => {
  if (weightUnit.unit === "US") {
    const lbs = parseFloat(baby.weightLbs || 0);
    const oz = parseFloat(baby.weightOz || 0);
    return lbs > 0 || oz > 0;
  }
  return parseFloat(baby.weightKg || 0) > 0;
};

/**
 * Validates birth event form
 * @param {Object} baby - Baby form data
 * @param {Object} weightUnit - Weight unit preference
 * @param {boolean} isLegacyMode - Whether using legacy mode
 * @returns {Object} Validation result with isValid and message
 */
export const validateBirthEventForm = (baby, weightUnit, isLegacyMode = false) => {
  // In legacy mode, be more flexible with validation
  if (isLegacyMode) {
    if (!baby.firstName && !baby.lastName) {
      return { isValid: false, message: 'Please enter at least a first or last name' };
    }
    return { isValid: true };
  }
  
  // Full validation for V2 or explicit saves
  const requiredFields = ['firstName', 'lastName', 'dateOfBirth', 'timeOfBirth', 'sex'];
  const missingFields = requiredFields.filter(field => !baby[field]);
  
  if (missingFields.length > 0) {
    return { isValid: false, message: `Please fill in: ${missingFields.join(', ')}` };
  }
  
  if (!validateWeight(baby, weightUnit)) {
    return { isValid: false, message: 'Please enter a valid birth weight' };
  }
  
  return { isValid: true };
};

/**
 * Parses labor notes from string or array
 * @param {string|Array} laborNotes - Labor notes data
 * @returns {Array} Array of note objects
 */
export const parseLaborNotes = (laborNotes) => {
  if (!laborNotes) return [];
  
  if (typeof laborNotes === 'string') {
    if (laborNotes.trim()) {
      try {
        return JSON.parse(laborNotes);
      } catch (err) {
        console.error('Error parsing labor notes:', err);
        return [];
      }
    }
    return [];
  }
  
  if (Array.isArray(laborNotes)) {
    return laborNotes;
  }
  
  return [];
};